import { IConfigRetailUser, IConfigStateGroups } from 'constants/interfaces'
import keys from 'constants/keys'
import { getContextName } from 'hooks/useContextName'
import { DeepDiveInsights, TaskInsightsResult } from 'redux/actions/tasks_v2'
import {
  countSubtaskStatuses,
  deriveTaskStatusValue,
  normalizeTaskStatusValue,
  SubtaskStatusCounts,
} from 'utils/taskStatusModel'

export type ContextRow = {
  contextId: string
  /** One of the 5 task statuses (`utils/taskStatusModel`) — derived from `statusCounts`. */
  status: number
  /** Per-subtask status counts behind `status` — feeds the breakdown capsules. */
  statusCounts: SubtaskStatusCounts
  taskContextId: string
  name: string
  typeCounts: Record<number, number>
  commentOids: string[]
  scoreDeduction: number | null
}

const CONTEXT_SORT_ORDER: Partial<Record<number, number>> = {
  [keys.TASK_STATUSES.DONE.value]: 0,
  [keys.TASK_STATUSES.APPROVED.value]: 1,
  [keys.TASK_STATUSES.DISAPPROVED.value]: 2,
  [keys.TASK_STATUSES.INPROGRESS.value]: 3,
  [keys.TASK_STATUSES.TODO.value]: 4,
}

interface IBuildContextRowsOptions {
  groupsObject: IConfigStateGroups | undefined
  retailUsersObject: { [key: string]: IConfigRetailUser } | undefined
  activeRecurrenceTs: string | null
  results: TaskInsightsResult[]
  /**
   * Number of subtasks in the task — the denominator for per-context status counts.
   * Optional: when the caller doesn't know it, the widest per-context row set is used instead
   * (every context is assigned the same subtasks, so the max row count is the best estimate).
   */
  subtaskCount?: number
  /**
   * st_id → subtask type, from `insights[tid].sub_tasks`. Used to resolve the type of a subtask that
   * the `insights/task` response omitted (see the union below), so its comment OID is still built.
   */
  subtaskTypes?: Record<string, number>
}

// Maps `deepDiveInsights` into the rows the preview renders — resolving context names,
// comment OIDs (image/video subtasks), per-type counts and averaged AI score deductions,
// then sorting by completion status. Shared by the dashboard and the feed self-bubble.
//
// The context status is recounted CLIENT-SIDE from the per-subtask statuses in
// `deepDiveInsights.insights[contextId].items` (the `data_rows` of `insights/task`) rather than
// taken from the server aggregate in `deepDiveInsights.contexts[contextId].status`. The server
// aggregate still uses the old 7-status model (it can return APPROVED_WITH_RESERVATION /
// REVIEWED_BY_AI), so it is normalized and used only when the rows cannot answer the question.
//
// Two sources feed the recount, unioned by st_id:
//   1. `insights/task` rows — fresh, but the backend omits AI-reviewed results (status -5)
//   2. `insights[tid].results` — has those rows, but can be stale (fetched once per session)
// A subtask still missing from both is genuinely unknown, so the count must NOT pad it with `todo`
// (that used to turn a finished context into "in progress"); the server aggregate decides instead.
export const buildContextRows = (
  deepDiveInsights: DeepDiveInsights | null | undefined,
  { groupsObject, retailUsersObject, activeRecurrenceTs, results, subtaskCount, subtaskTypes }: IBuildContextRowsOptions
): ContextRow[] => {
  if (!deepDiveInsights?.contexts || !groupsObject || !retailUsersObject) return []

  const effectiveSubtaskCount =
    subtaskCount ||
    Object.values(deepDiveInsights.insights || {}).reduce((max, entry) => Math.max(max, entry.items?.length || 0), 0)

  return Object.entries(deepDiveInsights.contexts)
    .map(([contextId, value]) => {
      const contextIdRecurrenceTs = `${contextId}${activeRecurrenceTs ? '_' + activeRecurrenceTs : ''}`
      const items = deepDiveInsights.insights?.[contextId]?.items

      const ctxResults = results.filter(
        (r) => r.context_id === contextId && (!activeRecurrenceTs || r.recurrence_ts === activeRecurrenceTs)
      )

      // Union by st_id. The task-list results fill the gaps first; the fresher `insights/task` rows
      // then win for any subtask present in both.
      const statusByStId = new Map<string, number>()
      const typeByStId = new Map<string, number>()
      ctxResults.forEach((result) => {
        if (!result.st_id) return
        statusByStId.set(result.st_id, result.status)
        const type = subtaskTypes?.[result.st_id]
        if (type !== undefined) typeByStId.set(result.st_id, type)
      })
      ;(items || []).forEach((item) => {
        if (!item.st_id) return
        statusByStId.set(item.st_id, item.status)
        typeByStId.set(item.st_id, item.type)
      })

      const knownStatuses = Array.from(statusByStId.values())
      // Only pad the missing subtasks with `todo` when every subtask is actually accounted for;
      // otherwise the denominator is what we know about.
      const isCountComplete = knownStatuses.length >= effectiveSubtaskCount
      const statusCounts = countSubtaskStatuses(
        knownStatuses,
        isCountComplete ? effectiveSubtaskCount : knownStatuses.length
      )
      // Derive from the rows when they can answer it: either every subtask is accounted for, or one of
      // the known ones is not finished (then the context is in progress no matter what the aggregate
      // says). Only when the rows are incomplete AND all of them are finished does the aggregate decide
      // — that is the "AI-reviewed row is missing" case, where padding with `todo` would invent a
      // never-started subtask.
      const canDeriveFromRows =
        knownStatuses.length > 0 && (isCountComplete || statusCounts.terminal < statusCounts.total)
      const status = canDeriveFromRows ? deriveTaskStatusValue(statusCounts) : normalizeTaskStatusValue(value.status)

      const commentOids = Array.from(typeByStId.entries())
        .filter(([, type]) => type === keys.SUBTASK_TYPES.IMAGE_SUBTASK || type === keys.SUBTASK_TYPES.VIDEO_SUBTASKS)
        .map(([stId]) => `${stId}_${contextIdRecurrenceTs}`)
      const deductions = ctxResults
        .map((r) => {
          const value = r.reservations?.score_deduction ?? r.score_deduction
          return typeof value === 'number' ? value : null
        })
        .filter((d): d is number => d !== null)
      const scoreDeduction =
        deductions.length > 0 ? Math.round(deductions.reduce((sum, d) => sum + d, 0) / deductions.length) : null

      return {
        contextId,
        status,
        statusCounts,
        taskContextId: value.taskContextId,
        name: getContextName(contextId, groupsObject, retailUsersObject),
        typeCounts: deepDiveInsights.insights?.[contextId]?.typeCounts || {},
        commentOids,
        scoreDeduction,
      }
    })
    .sort((a, b) => (CONTEXT_SORT_ORDER[a.status] ?? 99) - (CONTEXT_SORT_ORDER[b.status] ?? 99))
}
